import { useState, useEffect, useMemo } from 'react';
import { __ } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import CptTable from './CptTable';
import { apiFetch } from '../scripts/api';

const col = createColumnHelper();

const typeLabel = ( type ) =>
	'suggestion' === type
		? __( 'Suggest changes', 'anacrm' )
		: __( 'Reclaim org', 'anacrm' );

const buttonClass =
	'inline-block px-2 py-1 text-xs border-2 border-green-dark bg-gray-light hover:bg-yellow font-semibold whitespace-nowrap !no-underline';

/**
 * "Pending orgs" section of the profile page (staff only): reclaims and
 * suggested changes waiting for review, newest first.
 */
export default function PendingOrgsTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );

	useEffect( () => {
		apiFetch( '/anacrm/v1/org-requests' )
			.then( setData )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const columns = useMemo(
		() => [
			col.accessor( 'orgName', {
				header: __( 'Organisation', 'anacrm' ),
				cell: ( { getValue } ) => (
					<span className="font-semibold">{ getValue() }</span>
				),
			} ),
			col.accessor( ( row ) => typeLabel( row.type ), {
				id: 'type',
				header: __( 'Type', 'anacrm' ),
			} ),
			col.accessor( 'mail', { header: __( 'Email', 'anacrm' ) } ),
			col.accessor( 'firstname', {
				header: __( 'Firstname', 'anacrm' ),
			} ),
			col.accessor( 'lastname', { header: __( 'Lastname', 'anacrm' ) } ),
			col.accessor( 'date', {
				header: __( 'Date', 'anacrm' ),
				sortingFn: 'alphanumeric',
				cell: ( { getValue } ) => {
					const d = new Date( getValue() );
					return isNaN( d.getTime() ) ? '' : d.toLocaleString();
				},
			} ),
			col.display( {
				id: 'actions',
				header: '',
				enableSorting: false,
				cell: ( { row } ) => (
					<div className="flex flex-col gap-1 items-start">
						<a
							href={ row.original.publishedUrl }
							className={ buttonClass }
						>
							{ __( 'See published version', 'anacrm' ) }
						</a>
						<a
							href={ row.original.reviewUrl }
							className={ buttonClass }
						>
							{ __( 'Review proposal', 'anacrm' ) }
						</a>
					</div>
				),
			} ),
		],
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	if ( ! loading && data.length === 0 ) {
		return <p>{ __( 'There are no orgs pending review.', 'anacrm' ) }</p>;
	}

	return (
		<CptTable
			columns={ columns }
			data={ data }
			isLoading={ loading }
			searchPlaceholder={ __( 'Search pending orgs…', 'anacrm' ) }
			initialSorting={ [ { id: 'date', desc: true } ] }
			fitWidth
		/>
	);
}
